import React, { useState, useEffect } from "react";
import { getSupabase, supabasePublic } from "./supabaseClient";
import { getSignedFilesUrl } from "../utils/signedUrls";
import { buildReportsFolder } from "../utils/reportsPath";
import { beginActivity, endActivity } from "../utils/activityTracker";
import {
  VStack,
  Input,
  Button,
  Checkbox,
  HStack,
  Select,
  Box,
  Text,
  Progress,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Center,
  Spinner,
} from "@chakra-ui/react";
import {
  bucketName_files,
  tableName,
  prompts,
  file_reference_column,
  supabaseUrl,
  defaultFolderNameColumns,
  allowedFileTypes,
  BASE_URLS,
  promptUserInput,
  getConcatenatedInput,
} from "./env.js";
import jsonToFormattedText from "./JSONToMarkdown.js";
import Modal from "react-modal";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faFile,
  faArrowUp,
  faArrowDown,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ResultsTable from "./table/ResultsTable.js";

// Fetch the stored concatenated input or use the default folder name
const storedConcatenatedInput = getConcatenatedInput();

// Maximum file size in bytes (10MB)
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Sanitize path components for Supabase storage (removes invalid characters)
const sanitizePathComponent = (str) => {
  if (!str) return '';
  // Replace invalid characters for Supabase storage keys
  // Invalid chars: @, comma, spaces, colons, and other special chars
  return String(str)
    .replace(/[^a-zA-Z0-9._-]/g, '_')  // Replace invalid chars with underscore
    .replace(/_+/g, '_')                // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '')            // Remove leading/trailing underscores
    .substring(0, 255);                 // Limit length
};

// Sanitize filename while preserving extension
const sanitizeFilename = (filename) => {
  if (!filename) return 'file';
  const parts = filename.split('.');
  const extension = parts.length > 1 ? parts.pop() : '';
  const nameWithoutExt = parts.join('.');
  const sanitizedName = sanitizePathComponent(nameWithoutExt);
  return extension ? `${sanitizedName}.${extension}` : sanitizedName;
};

const FileUploader = () => {
  const { id } = useParams();
  const [files, setFiles] = useState([]);
  const [previousFolderPath, setPreviousFolderPath] = useState("");
  const [folderName, setFolderName] = useState(storedConcatenatedInput);
  const [useSameFolder, setUseSameFolder] = useState(true);
  const [uploadedFolderName, setUploadedFolderName] = useState("");
  const [folders, setFolders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedColumns, setSelectedColumns] = useState(
    prompts.reduce((acc, [promptName]) => {
      acc[promptName] = true;
      return acc;
    }, {})
  );
  const [results, setResults] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentFolderFiles, setCurrentFolderFiles] = useState([]);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [duplicateFilePath, setDuplicateFilePath] = useState("");
  const [folderToUse, setFolderToUse] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPatientData = async () => {
      if (!id) return;

      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from(tableName)
          .select("*")
          .eq("id", id)
          .single();

        if (error) {
          console.error("Error fetching patient data:", error);
          toast({
            title: "Error",
            description: "Failed to fetch patient data",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          navigate("/admin-page");
          return;
        }

        if (data) {
          setResults([data]);
          setSelectedRows([true]);
          const defaultName = defaultFolderNameColumns
            .map((col) => data[col])
            .join("-");
          setFolderName(defaultName);
        }
      } catch (error) {
        console.error("Error:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        navigate("/admin-page");
      } finally {
        setLoading(false);
      }
    };

    fetchPatientData();
  }, [id, navigate, toast]);

  useEffect(() => {
    console.log("Initial folderName:", folderName);
    const fetchFolders = async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.storage
        .from(bucketName_files)
        .list("", { limit: 100 });

      if (error) {
        console.error("Error fetching folders from Supabase:", error);
        toast({
          title: "Error fetching folders",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        const folderNames = data
          .filter((item) => item.id === null)
          .map((folder) => folder.name);
        setFolders(folderNames);
      }
    };

    fetchFolders();
  }, [toast]);

  const validateFile = (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const errors = [];

    if (!allowedFileTypes.includes(fileExtension)) {
      errors.push(`File type ${fileExtension} is not supported. Allowed types: ${allowedFileTypes.join(', ')}`);
    }

    if (file.size > MAX_FILE_SIZE) {
      errors.push(`File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`);
    }

    return errors;
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    const errors = {};

    selectedFiles.forEach((file) => {
      const fileErrors = validateFile(file);
      if (fileErrors.length === 0) {
        validFiles.push(file);
      } else {
        errors[file.name] = fileErrors;
      }
    });

    setFiles(validFiles);
    setUploadErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast({
        title: "Invalid files detected",
        description: "Some files were rejected due to validation errors",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleViewFolderFiles = async (folderPath) => {
    const supabase = await getSupabase();
    setCurrentFolderPath(folderPath);
    if (!folderPath) {
      setCurrentFolderFiles([]);
      setModalIsOpen(true);
      return;
    }

    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
      setModalIsOpen(true);
    }
  };

  const handleViewJSONFiles = (jsonContent) => {
    try {
      const jsonData = JSON.parse(jsonContent);
      const formattedText = jsonToFormattedText(jsonData);
      openHTMLInNewTab(formattedText);
    } catch (error) {
      console.error("Error parsing JSON content:", error);
      alert("Error parsing JSON content");
    }
  };

  const openHTMLInNewTab = (htmlContent) => {
    const newWindow = window.open("", "_blank");
    newWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>HTML Viewer</title>
        </head>
        <body>
          <div id="content">${htmlContent}</div>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const handleFolderChange = (event) => {
    setFolderName(event.target.value);
  };

  const handleUseSameFolderChange = (event) => {
    setUseSameFolder(event.target.value === "true");
  };

  const handleSearch = async () => {
    const supabase = await getSupabase();
    console.log("Search query:", searchQuery);

    if (searchQuery.trim() === "") {
      alert("Please enter a search query");
      return;
    }

    const selectedColumnsArray = Object.keys(selectedColumns).filter(
      (key) => selectedColumns[key]
    );
    if (selectedColumnsArray.length === 0) {
      alert("Please select at least one column to search by");
      return;
    }

    // Get user session and check role
    const { data: sessionData, error: sessionError } =
      await supabase.auth.getSession();
    if (sessionError) {
      console.error("Error getting session:", sessionError);
      return;
    }

    const userId = sessionData?.session?.user?.id;

    // Get user's role from the users table
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("role")
      .eq("id", userId)
      .single();

    if (userError) {
      console.error("Error fetching user role:", userError);
      return;
    }

    const orConditions = selectedColumnsArray.map(
      (column) => `${column}.ilike.%${searchQuery}%`
    );

    try {
      let query = supabase
        .from(tableName)
        .select("*")
        .or(orConditions.join(","));

      // Only apply user_id filter if the user is not an admin
      if (userData.role !== "admin") {
        query = query.eq("user_id", userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching data from Supabase:", error);
        return;
      }

      setResults(data);
      setSelectedRows(new Array(data.length).fill(false));
    } catch (error) {
      console.error("Error searching data:", error);
    }
  };

  const handleCheckboxChange = (index) => {
    const updatedRows = [...selectedRows];
    updatedRows[index] = !updatedRows[index];
    setSelectedRows(updatedRows);

    const firstCheckedIndex = updatedRows.findIndex((row) => row);
    if (firstCheckedIndex !== -1) {
      const selectedRow = results[firstCheckedIndex];
      const defaultName = defaultFolderNameColumns
        .map((col) => selectedRow[col])
        .join("-");
      setFolderName(defaultName);
    }
  };

  const handleUploadInner = async () => {
    if (!files.length) {
      toast({
        title: "No files selected",
        description: "Please select at least one file to upload",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const selectedRow = results[selectedRows.findIndex((row) => row)];
      if (!selectedRow || !selectedRow.id) {
        toast({
          title: "No patient selected",
          description: "Please select a patient row",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // The same folder View Reports reads: the row's `reports` column when set,
      // else the canonical visit folder (New/Reports/{schema/}{name}_{EHR}/{MM-DD-YY}).
      // Both env columns (data_column, file_reference_column) are "reports", so
      // the old `${reports}_${reports}` name sent files to a junk folder.
      const existingFolder = String(selectedRow[file_reference_column] || "").trim().replace(/\/+$/, "");
      const reportsFolder = existingFolder || buildReportsFolder(selectedRow);
      if (!reportsFolder) {
        toast({
          title: "No reports folder",
          description: "This patient needs a name and a visit date before files can be uploaded.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      let folderSaved = Boolean(existingFolder);

      for (const file of files) {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const isJson = fileExtension === "json";
        // JSON files keep their own tree, mirroring the reports layout.
        const targetFolder = isJson ? reportsFolder.replace(/^New\/Reports\//, "New/JSON/") : reportsFolder;
        const sanitizedFileName = sanitizeFilename(file.name);
        const filePath = `${targetFolder}/${sanitizedFileName}`;

        try {
          setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
          await uploadFileToStorage(filePath, file, bucketName_files);
          setUploadSuccess(prev => ({ ...prev, [file.name]: true }));
          setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));

          // Point the row at its reports folder once, when it had none. A JSON
          // folder is never written there: it would hide the patient's reports.
          if (!isJson && !folderSaved) {
            await updateSupabaseTable({ [file_reference_column]: reportsFolder }, selectedRow.id);
            folderSaved = true;
          }
          
          // Automatically classify report files (not JSON files)
          if (fileExtension !== "json") {
            try {
              console.log(`🔍 Starting classification for file: ${file.name} at path: ${filePath}`);
              const classificationResult = await classifyUploadedFile(filePath);
              console.log(`✅ Successfully classified ${file.name}:`, classificationResult);
              // Classification successful - no alert needed
            } catch (classifyError) {
              // Log detailed error but don't fail the upload
              console.error(`❌ Failed to classify file ${file.name}:`, classifyError);
              console.error(`Error details:`, {
                message: classifyError.message,
                stack: classifyError.stack,
                filePath: filePath
              });
              // Don't show alert for classification failure - upload was successful
              console.warn(`⚠️ Classification failed for ${file.name}, but file uploaded successfully`);
            }
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          setUploadErrors(prev => ({ ...prev, [file.name]: [error.message] }));
          toast({
            title: `Failed to upload ${file.name}`,
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error("Error during file upload:", error);
      toast({
        title: "Upload failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpload = async () => {
    beginActivity("uploading");
    try {
      await handleUploadInner();
    } finally {
      endActivity("uploading");
    }
  };

  const uploadFileToStorage = async (filePath, file, bucketName) => {
    const supabase = await getSupabase();
    
    // Determine content type based on file extension (minimal - only 3 image types)
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const contentType = 
      fileExtension === 'png' ? 'image/png' :
      fileExtension === 'jpg' || fileExtension === 'jpeg' ? 'image/jpeg' :
      fileExtension === 'pdf' ? 'application/pdf' :
      fileExtension === 'csv' ? 'text/csv' :
      fileExtension === 'txt' ? 'text/plain' :
      fileExtension === 'html' ? 'text/html' :
      fileExtension === 'json' ? 'application/json' :
      file.type || 'application/octet-stream';
    
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, {
        contentType: contentType,
        onUploadProgress: (progress) => {
          const percent = Math.round((progress.loaded / progress.total) * 100);
          setUploadProgress(prev => ({ ...prev, [file.name]: percent }));
        }
      });

    if (uploadError) {
      console.error("Error uploading file to Supabase:", uploadError);
      throw new Error(uploadError.message || `Failed to upload ${file.name}`);
    }

    console.log("File uploaded successfully:", uploadData);
    setUploadedFolderName(filePath.split('/').slice(1).join('/'));
  };

  const updateSupabaseTable = async (updateObject, rowId) => {
    const supabase = await getSupabase();
    const { data: updateData, error: updateError } = await supabase
      .from(tableName)
      .update(updateObject)
      .eq("id", rowId);

    if (updateError) {
      console.error("Error updating rows in Supabase:", updateError);
      throw new Error(updateError.message);
    }

    console.log("Rows updated successfully:", updateData);
  };

  const classifyUploadedFile = async (filePath) => {
    try {
      const hostname = window.location.hostname;
      const backendUrl = hostname === "localhost" 
        ? BASE_URLS.local.backend 
        : BASE_URLS.render.backend;
      
      console.log(`🔍 Calling classification endpoint: ${backendUrl}/api/ai-assistant/classify-file/`);
      console.log(`🔍 File path: ${filePath}`);
      
      // Get selected models from localStorage to use during classification (OCR)
      let modelIds = null;
      try {
        const savedModels = localStorage.getItem("aiAssistant_selectedModels");
        if (savedModels) {
          modelIds = JSON.parse(savedModels);
        }
      } catch (e) {
        console.warn("Failed to read selectedModels from localStorage:", e);
      }
      
      const response = await fetch(`${backendUrl}/api/ai-assistant/classify-file/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_path: filePath,
          modelIds: modelIds,
          modelId: modelIds && modelIds.length > 0 ? modelIds[0] : null
        })
      });

      console.log(`🔍 Classification response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Classification failed with status ${response.status}:`, errorText);
        let errorMessage = `Classification failed: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If parsing fails, use the text as is
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log(`✅ File ${filePath} classified as: ${data.classification} (cached: ${data.cached})`);
      return data;
    } catch (error) {
      console.error(`❌ Error classifying file ${filePath}:`, error);
      // Re-throw with more context
      throw new Error(`Classification error: ${error.message}`);
    }
  };

  const handleConfirmUpload = () => {
    // Append unique identifier to the file name
    const uniqueFileName = `${files[0].name.split(".")[0]}_${Date.now()}.${files[0].name
      .split(".")
      .pop()}`;
    const uniqueFilePath = `${folderToUse}/${uniqueFileName}`;
    uploadFileToStorage(uniqueFilePath, files[0], bucketName_files);
    setShowConfirmation(false);
  };

  const handleCancelUpload = () => {
    setShowConfirmation(false);
  };

  const handleGoUpOneLevel = async () => {
    const supabase = await getSupabase();
    setPreviousFolderPath(currentFolderPath);
    setCurrentFolderPath("");
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list("");

    if (error) {
      console.error("Error fetching folders from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
    }
  };

  const handleGoDownOneLevel = async () => {
    handleViewFolderFiles(previousFolderPath);
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <VStack spacing={4} align="stretch" p={4}>
      <Text fontSize="2xl" fontWeight="bold" mb={4}>
        Upload Files for Patient
      </Text>
      {/* Step 1: Pick a file */}
      <Box>
        <Input
          type="file"
          accept={allowedFileTypes.map((type) => `.${type}`).join(",")}
          onChange={handleFileChange}
          multiple
        />
        {files.length > 0 && (
          <VStack mt={2} spacing={2} align="stretch">
            {files.map((file) => (
              <Box key={file.name} p={2} borderWidth={1} borderRadius="md">
                <HStack justify="space-between">
                  <Text>{file.name}</Text>
                  {uploadSuccess[file.name] ? (
                    <FontAwesomeIcon icon={faCheck} color="green" />
                  ) : uploadErrors[file.name] ? (
                    <FontAwesomeIcon icon={faTimes} color="red" />
                  ) : null}
                </HStack>
                {uploadProgress[file.name] !== undefined && (
                  <Progress
                    value={uploadProgress[file.name]}
                    size="sm"
                    colorScheme={uploadSuccess[file.name] ? "green" : "blue"}
                    mt={2}
                  />
                )}
                {uploadErrors[file.name] && (
                  <Alert status="error" size="sm" mt={2}>
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Upload failed</AlertTitle>
                      <AlertDescription>
                        {uploadErrors[file.name].join(", ")}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
              </Box>
            ))}
          </VStack>
        )}
      </Box>
      <HStack spacing={4}>
        {/* Step 2: Search for rows */}
        <Input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <Button onClick={handleSearch}>Search Rows</Button>
      </HStack>

      {results.length > 0 && (
        <ResultsTable
          results={results}
          setResults={setResults}
          prompts={prompts}
          selectedRows={selectedRows}
          handleCheckboxChange={handleCheckboxChange}
          handleViewJSONFiles={handleViewJSONFiles}
          handleViewFolderFiles={handleViewFolderFiles}
          currentFolderPath={currentFolderPath}
          previousFolderPath={previousFolderPath}
          handleGoUpOneLevel={handleGoUpOneLevel}
          handleGoDownOneLevel={handleGoDownOneLevel}
          supabaseUrl={supabaseUrl}
          bucketName_files={bucketName_files}
          modalIsOpen={modalIsOpen}
          setModalIsOpen={setModalIsOpen}
          currentFolderFiles={currentFolderFiles}
        />
      )}

      {/* Step 3: Pick the folder name */}
      <Select
        onChange={handleFolderChange}
        placeholder="Select existing folder or enter a new one"
      >
        {folders.map((folder, index) => (
          <option key={index} value={folder}>
            {folder}
          </option>
        ))}
        <option value="">New Folder</option>
      </Select>
      <Input
        placeholder="Folder Name"
        value={folderName}
        onChange={handleFolderChange}
        isDisabled={useSameFolder && uploadedFolderName}
      />

      {/* Step 4: Upload file */}
      <Button
        colorScheme="blue"
        onClick={handleUpload}
        isDisabled={files.length === 0}
      >
        Upload Files
      </Button>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel="View Reports"
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "50%",
            maxHeight: "80vh",
            overflowY: "auto",
          },
        }}
      >
        <h2 style={{ fontSize: "24px" }}>
          <FontAwesomeIcon icon={faFolder} /> Files in Folder:{" "}
          {currentFolderPath}
        </h2>
        <Button
          onClick={handleGoUpOneLevel}
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          <FontAwesomeIcon icon={faArrowUp} /> Up One Level
        </Button>
        <ul style={{ listStyleType: "none", padding: 0 }}>
          {currentFolderFiles.map((file, index) => (
            <li key={index} style={{ marginBottom: "10px" }}>
              <FontAwesomeIcon
                icon={/\.(pdf|csv|txt)$/i.test(file.name) ? faFile : faFolder}
                style={{ marginRight: "8px", color: "#d3d3d3" }}
              />
              <a
                onClick={async (e) => {
                  e.preventDefault();
                  if (/\.(pdf|csv|txt)$/i.test(file.name)) {
                    // It's a file — open via a signed URL (works on a private bucket)
                    const filePath = currentFolderPath
                      ? `${currentFolderPath}/${file.name}`
                      : file.name;
                    const url = await getSignedFilesUrl(filePath);
                    if (url) window.open(url, "_blank", "noopener,noreferrer");
                  } else {
                    // It's a folder, navigate to the folder contents
                    const newPath = currentFolderPath
                      ? `${currentFolderPath}/${file.name}`
                      : file.name;
                    console.log("Navigating to folder contents", newPath);
                    handleViewFolderFiles(newPath);
                  }
                }}
                href="#"
                style={{ cursor: "pointer", color: "#000" }}
              >
                {file.name}
              </a>
            </li>
          ))}
        </ul>
        {previousFolderPath && (
          <Button
            onClick={handleGoDownOneLevel}
            style={{ marginTop: "10px", marginBottom: "10px" }}
          >
            <FontAwesomeIcon icon={faArrowDown} /> Down One Level
          </Button>
        )}
        <button
          onClick={() => setModalIsOpen(false)}
          style={{
            marginTop: "20px",
            padding: "6px 12px",
            background: "#007BFF",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </Modal>

      <Modal
        isOpen={showConfirmation}
        onRequestClose={handleCancelUpload}
        contentLabel="Confirm Upload"
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "40%",
            maxHeight: "40vh",
            overflowY: "auto",
          },
        }}
      >
        <h2>Confirm Upload</h2>
        <p>
          A file with the same name already exists in this folder. Are you sure
          you want to upload this file?
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "20px",
          }}
        >
          <button
            onClick={handleCancelUpload}
            style={{
              padding: "6px 12px",
              background: "#dc3545",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmUpload}
            style={{
              padding: "6px 12px",
              background: "#007BFF",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Yes
          </button>
        </div>
      </Modal>
    </VStack>
  );
};

export default FileUploader;
